<template>
  <div v-if="$featureFlags.isEnabled('meeting-completion-override')" class="mark-complete-manual-override">
    <p>
      <i class="fas fa-check-circle" :class="{ 'complete': isComplete }" aria-hidden="true"></i>
      <span v-if="isComplete">
        Meeting was marked as complete without learner action or signature
      </span>
      <span v-else>
        Mark this meeting as <b>complete</b> without the learner taking action
      </span>
    </p>
    <span id="mark-as-incomplete" class="sr-only">Mark as incomplete</span>
    <button v-if="isComplete" class="btn btn-success"
      @click="markIncomplete"
      aria-describedby="mark-as-incomplete"
    >
      Completed <i class="fas fa-check" aria-hidden="true"></i>
    </button>
    <button v-else class="btn btn-outlined"
      @click="showModal = true"
    >
      Mark as Complete
    </button>
    <k-modal :show="showModal" @close="showModal = false">
      <template #header>
        <h3>Mark Complete</h3>
      </template>
      <template #body>
        <k-disclaimer type="warning">
          This will mark the meeting as complete without the learner having done so themselves.
          <br/>
          This should only be considered as a <b>last resort</b> to satisfy compliance requirements
          if the learner is unavailable and all alternatives have been exhausted.
        </k-disclaimer>
        <k-form :columns="10">
          <k-form-section :column-span="10" legend="Manual Override">
            <k-form-field label="Note" :required="true" :has-error="!note" :field-id="`manual-override-note-${$.uid}`">
              <p>
                The following note will be added to the learning record to detail the reason for the manual override
              </p>
              <k-text-editor v-model="note" :max-length="1024" :element-id="`manual-override-note-${$.uid}`"></k-text-editor>
            </k-form-field>
          </k-form-section>
        </k-form>
      </template>
      <template #footer>
        <button class="btn btn-secondary" @click="showModal = false">Cancel</button>
        <button class="btn btn-primary" @click="markComplete">Confirm</button>
      </template>
    </k-modal>
  </div>
</template>

<style scoped>
.mark-complete-manual-override {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mark-complete-manual-override p {
  font-style: italic;
}

.mark-complete-manual-override i.complete {
  color: var(--kate-success);
}
</style>

<script>
import useGlobalStore from '@stores/global';
import KModal from '@base-components/k-modal.vue';
import KTextEditor from '@base-components/k-text-editor.vue';
import { KForm, KFormSection, KFormField } from '@base-components/forms';
import KDisclaimer from '@base-components/k-disclaimer.vue';

export default {
  components: {
    KModal,
    KTextEditor,
    KForm,
    KFormSection,
    KFormField,
    KDisclaimer,
  },

  props: {
    learnerId: {
      type: Number,
      required: true,
    },
    moduleMeetingId: {
      type: Number,
      required: true,
    },
    isComplete: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      store: useGlobalStore(),
      showModal: false,
      note: 'Marked complete by SDM as learner unavailable.',
    };
  },

  computed: {
    validForm() {
      return Boolean(this.note);
    },
    endpointRoot() {
      return `/api/curriculum/admin/meetings/${this.moduleMeetingId}/users/${this.learnerId}`;
    },
    logPayload() {
      return {
        learnerId: this.learnerId,
        moduleMeetingId: this.moduleMeetingId,
      };
    },
  },

  methods: {
    closeModal() {
      // Close modal and reset note
      this.showModal = false;
      this.note = 'Marked complete by SDM as learner unavailable.';
    },
    markComplete() {
      if (!this.validForm) {
        return;
      }
      this.$Loading.start();
      this.$logger.info('Marking meeting as complete without learner signature', this.logPayload);
      this.$http.put(`${this.endpointRoot}/complete`, {
        manual_completion_note: this.note,
      }).then(() => {
        this.$logger.info('Meeting marked as complete', this.logPayload);
        this.$ktoast.success('Meeting marked as complete');
        this.$emit('update');
      }).catch(err => {
        this.$logger.autowarn('Error marking meeting as complete', this.logPayload, err);
        this.$ktoast.showError(err, true);
      }).then(() => {
        this.closeModal();
      });
    },
    markIncomplete() {
      this.$Loading.start();
      this.$http.put(`${this.endpointRoot}/incomplete`, {}).then(() => {
        this.$logger.info('Meeting marked as incomplete', this.logPayload);
        this.$ktoast.success('Meeting marked as incomplete');
        this.$emit('update');
      }).catch(err => {
        this.$logger.autowarn('Error marking meeting as incomplete', this.logPayload, err);
        this.$ktoast.showError(err, true);
      });
    },
  },
};
</script>
